// SujetoList.jsx
import { useEffect, useState, useCallback } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import DashboardLayout from "../../controls/DashboardLayout";
import DashboardNavbar from "../../controls/DashboardNavbar";
import MDBox from "../../controls/MDBox";
import { Grid } from "@mui/material";
import { Card } from "react-bootstrap";
import MDTypography from "../../controls/MDTypography";
import ManualAyuda from "../../controls/ManualAyuda";
import MDButton from "../../controls/MDButton";
import MDInput from "../../controls/MDInput";
import DataTable from "../../controls/Tables/DataTable";
import { BuildingFillAdd, PencilSquare, People } from "react-bootstrap-icons";
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import EditIcon from '@mui/icons-material/Edit';
import { postData, pathData, deleteData } from "../../Utils/Http/api";
import CircularProgress from '@mui/material/CircularProgress';
import { Link, useNavigate } from "react-router-dom";
import { fetchSujetoContactoTipos } from "../../Utils/SujetoContactoTipo";
import { fetchSujetoContactoOrigenes } from "../../Utils/SujetoContactoOrigen";
import { fetchSujetoContactoPrioridades } from "../../Utils/SujetoContactoPrioridad";
import { Close, Edit, EditNote } from "@mui/icons-material";
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

function SujetoList() {
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [rawData, setRawData] = useState([]); // Datos sin procesar
  const [filters, setFilters] = useState({ nombre: "", identificacion: "", dni: "", email: "", telefono: "", domicilio: "" });
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({ cuit: "", dni: "", both: "" });
  const navigate = useNavigate();
  const [sujetoId, setSujetoId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const handleAdd = () => navigate("/SujetoAdd");

  // Función auxiliar para ordenar contactos por prioridad
  const sortContactosByPriority = (contactos) => {
    if (!Array.isArray(contactos)) return [];
    return [...contactos].sort((a, b) => {
      const prioA = a.idSujetoPrioridadContacto || 0;
      const prioB = b.idSujetoPrioridadContacto || 0;
      
      // Si ambos son null/0, mantener orden original
      if (prioA === 0 && prioB === 0) return 0;
      // Si A es null/0, va al final
      if (prioA === 0) return 1;
      // Si B es null/0, va al final
      if (prioB === 0) return -1;
      // Orden ascendente por prioridad
      return prioA - prioB;
    });
  };

  // Función para procesar los datos y generar las filas
  const processDataToRows = useCallback((list) => {
    const data = list.map((item) => {
      // Ordenar contactos por prioridad
      const sortedContactos = sortContactosByPriority(item.contactos);
      
      const detalle = (
        <MDBox>
          <MDTypography variant="h6" color="text" fontWeight="medium">
            {item.nombre}
          </MDTypography>
          <MDTypography variant="caption" color="success" fontWeight="medium">
            {item.tipoIdentificacion}  -  {item.identificacion}
          </MDTypography>

          <MDBox mt={1}>
            {Array.isArray(sortedContactos) && sortedContactos.length > 0 ? (
              sortedContactos.map((c) => (
                <MDBox key={c.idSujetoContacto} display="flex" alignItems="center" gap={1} mb={0.5}>
                  <MDTypography variant="caption" color="text" fontWeight="medium">
                    {c.tipoContacto} ({c.origen})
                    {c.prioridadNombre && (
                      <span style={{ 
                        color: c.prioridadNombre?.toLowerCase() === 'prioritario' ? '#1976d2' : '#ed6c02'
                      }}>
                        {` ${c.prioridadNombre}`}
                      </span>
                    )}
                  </MDTypography>
                  <MDTypography variant="caption" color="text" sx={{ ml: 1 }}>
                    {c.contacto}
                  </MDTypography>
                  <MDBox>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenEdit(c, item.idSujeto)}
                      aria-label="editar contacto"
                      sx={{ padding: '3px', '& .MuiSvgIcon-root': { fontSize: 16 } }}
                    >
                      <EditIcon color="info" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteContacto(c.idSujetoContacto)}
                      aria-label="eliminar contacto"
                      sx={{ padding: '3px', '& .MuiSvgIcon-root': { fontSize: 16 } }}
                    >
                      <Close color="error" />
                    </IconButton>
                  </MDBox>
                </MDBox>
              ))
            ) : (
              <MDTypography variant="caption" color="text">
                Sin contactos
              </MDTypography>
            )}
          </MDBox>

          <MDBox mt={1}>
            <IconButton
              size="small"
              color="info"
              onClick={() => handleOpenAdd(item.idSujeto)}
              aria-label="agregar contacto"
              sx={{ padding: '6px', borderRadius: 1, border: '1px solid', borderColor: 'rgba(0,0,0,0.08)' }}
            >
              <EditNote color="success" fontSize="small" />
            </IconButton>
          </MDBox>
        </MDBox>
      );

      const action = (
        <MDBox display="flex" gap={1} alignItems="center" justifyContent="center">
          <MDBox>
            <Link to={`../SujetoEdit/${item.idSujeto}`}>
              <MDButton variant="text" color="dark" size="large">
                <PencilSquare color="blue" />
              </MDButton>
            </Link>
          </MDBox>
        </MDBox>
      );

      const estadoTipo = (
        <MDBox display="flex" flexDirection="column" gap={0.5}>
          <MDTypography variant="caption" color="text" fontWeight="medium">
            Concurso: {item.concursado ? 'SI' : 'NO'}
          </MDTypography>
          <MDTypography variant="caption" color="text" fontWeight="medium">
            Fallecido: {item.fallecido ? 'SI' : 'NO'}
          </MDTypography>
          <MDTypography variant="caption" color="text" fontWeight="medium">
            Quebrado: {item.quebrado ? 'SI' : 'NO'}
          </MDTypography>
          <MDTypography variant="caption" color="text" fontWeight="medium">
            Tipo Persona: {item.tipoPersona === 1 ? 'PF' : item.tipoPersona === 2 ? 'PJ' : ''}
          </MDTypography>
        </MDBox>
      );

      const domicilio = (
        <MDTypography variant="caption" color="text" fontWeight="medium">
          {item.domicilio || ''}
        </MDTypography>
      );

      return { detalle, estadoTipo, domicilio, action };
    });

    setRows(data);
    setRefreshKey((k) => k + 1);
  }, []);

  // Nota: aceptamos los filtros actuales por parámetro para evitar cierres obsoletos (stale closures)
  const fetchData = useCallback(async (useFilters = true, currentFilters = null) => {
    setLoading(true);
    try {
      const effectiveFilters = currentFilters ?? filters;
      const req = useFilters
        ? { 
            nombre: effectiveFilters.nombre || "", 
            identificacion: effectiveFilters.identificacion || "",
            dni: effectiveFilters.dni || "",
            email: effectiveFilters.email || "",
            telefono: effectiveFilters.telefono || "",
            domicilio: effectiveFilters.domicilio || ""
          }
        : { nombre: "", identificacion: "", dni: "", email: "", telefono: "", domicilio: "" };
      const response = await postData("/SujetoContactosbyParams", req);
      const list = response?.value || [];

      // Almacenar datos sin procesar
      setRawData(list);
      // Procesar y mostrar
      processDataToRows(list);
      setColumns([
        { Header: "Sujeto y Contactos", accessor: "detalle", align: "left" },
        { Header: "Estado/Tipo", accessor: "estadoTipo", align: "left" },
        { Header: "Domicilio", accessor: "domicilio", align: "left" },
        { Header: "Acciones", accessor: "action", align: "center" },
      ]);
      return list;
    } catch (ex) {
      console.error("Error cargando Sujetos:", ex);
      setError(ex);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // No cargar datos automáticamente al iniciar
    // El usuario debe ingresar al menos un filtro y presionar Buscar
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    
    // Solo permitir números
    if ((name === "identificacion" || name === "dni") && value && !/^\d*$/.test(value)) {
      return;
    }
    
    setFilters({ ...filters, [name]: value });
    
    // Validaciones en tiempo real
    const newErrors = { cuit: "", dni: "", both: "" };
    
    if (name === "identificacion") {
      if (value && value.length !== 11 && value.length > 0) {
        newErrors.cuit = "El CUIT debe tener exactamente 11 dígitos";
      }
      if (value && filters.dni) {
        newErrors.both = "Solo puede filtrar por CUIT o DNI, no ambos";
      }
    }
    
    if (name === "dni") {
      if (value && value.length !== 8 && value.length > 0) {
        newErrors.dni = "El DNI debe tener exactamente 8 dígitos";
      }
      if (value && filters.identificacion) {
        newErrors.both = "Solo puede filtrar por CUIT o DNI, no ambos";
      }
    }
    
    // Validar si ambos están llenos
    const updatedFilters = { ...filters, [name]: value };
    if (updatedFilters.identificacion && updatedFilters.dni) {
      newErrors.both = "Solo puede filtrar por CUIT o DNI, no ambos";
    }
    
    setValidationErrors(newErrors);
  };

  const handleSearch = async () => {
    // Validar antes de buscar
    const errors = { cuit: "", dni: "", both: "" };
    
    // Validar que al menos un filtro tenga valor
    if (!filters.nombre && !filters.identificacion && !filters.dni && !filters.email && !filters.telefono && !filters.domicilio) {
      setSnackbar({ open: true, msg: "Debe ingresar al menos un filtro para realizar la búsqueda", severity: "warning" });
      return;
    }
    
    if (filters.identificacion && filters.dni) {
      errors.both = "Solo puede filtrar por CUIT o DNI, no ambos";
      setValidationErrors(errors);
      return;
    }
    
    if (filters.identificacion && filters.identificacion.length !== 11) {
      errors.cuit = "El CUIT debe tener exactamente 11 dígitos";
      setValidationErrors(errors);
      return;
    }
    
    if (filters.dni && filters.dni.length !== 8) {
      errors.dni = "El DNI debe tener exactamente 8 dígitos";
      setValidationErrors(errors);
      return;
    }
    
    // El teléfono se busca completo: código de área + número, 10 dígitos, sin el 0 inicial ni el 15.
    if (filters.telefono && filters.telefono.replace(/\D/g, "").length !== 10) {
      setSnackbar({ open: true, msg: "Ingresá el teléfono completo: 10 dígitos, sin el 0 inicial y sin el 15 (ej. 3516339290)", severity: "warning" });
      return;
    }

    // El domicilio solo no es selectivo (hay millones de sujetos): se combina con otro filtro.
    if (filters.domicilio && !filters.nombre && !filters.identificacion && !filters.dni && !filters.email && !filters.telefono) {
      setSnackbar({ open: true, msg: "El domicilio debe combinarse con otro filtro (nombre, CUIT, DNI, email o teléfono)", severity: "warning" });
      return;
    }

    setValidationErrors({ cuit: "", dni: "", both: "" });
    await fetchData(true, filters);
  };

  const handleClear = async () => {
    setFilters({ nombre: "", identificacion: "", dni: "", email: "", telefono: "", domicilio: "" });
    setValidationErrors({ cuit: "", dni: "", both: "" });
    setRows([]);
    setColumns([]);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Edit dialog state
  const [openEdit, setOpenEdit] = useState(false);
  const [contactoEdit, setContactoEdit] = useState(null);
  const [tiposContacto, setTiposContacto] = useState([]);
  const [origenesContacto, setOrigenesContacto] = useState([]);
  const [prioridadesContacto, setPrioridadesContacto] = useState([]);
  const [selectedTipo, setSelectedTipo] = useState(null);
  const [selectedOrigen, setSelectedOrigen] = useState(null);
  const [selectedPrioridad, setSelectedPrioridad] = useState(null);
  const [valorContacto, setValorContacto] = useState("");
  // Add dialog state
  const [openAdd, setOpenAdd] = useState(false);
  const [addSelectedTipo, setAddSelectedTipo] = useState(null);
  const [addSelectedOrigen, setAddSelectedOrigen] = useState(null);
  const [addSelectedPrioridad, setAddSelectedPrioridad] = useState(null);
  const [addValorContacto, setAddValorContacto] = useState("");
  const [addSujetoId, setAddSujetoId] = useState(null);
  // Delete confirmation dialog
  const [openDelete, setOpenDelete] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, msg: "", severity: "success" });

  const handleOpenEdit = async (c, idSujeto) => {
    // c is the contact object
    setContactoEdit(c);
    setValorContacto(c.contacto || "");
    setSujetoId(idSujeto || null);
    try {
      // Prefer the new async helpers (or the hook default if other components rely on it)
      const tipos = await fetchSujetoContactoTipos();
      const origenes = await fetchSujetoContactoOrigenes();
      const prioridades = await fetchSujetoContactoPrioridades();
      const tiposList = (tipos || []).map(t => ({ id: t.idSujetoContactoTipo, label: t.descripcion, activo: t.activo }));
      const origenesList = (origenes || []).map(o => ({ id: o.idSujetoContactoOrigen, label: o.descripcion, activo: o.activo }));
      const prioridadesList = (prioridades || []).map(p => ({ id: p.idSujetoPrioridadContacto, label: p.descripcion, activo: p.activo }));
      setTiposContacto(tiposList);
      setOrigenesContacto(origenesList);
      setPrioridadesContacto(prioridadesList);

      // preselect by id if present
      const preTipo = tiposList.find(x => x.id === c.idSujetoContactoTipo) || null;
      const preOrigen = origenesList.find(x => x.id === c.idSujetoContactoOrigen) || origenesList.find(x => x.label === c.origen) || null;
      const prePrioridad = prioridadesList.find(x => x.id === c.idSujetoPrioridadContacto) || null;
      setSelectedTipo(preTipo);
      setSelectedOrigen(preOrigen);
      setSelectedPrioridad(prePrioridad);
    } catch (ex) {
      console.error('Error cargando datos para editar contacto:', ex);
    }
    setOpenEdit(true);
  };

  const handleCloseEdit = () => {
    setOpenEdit(false);
    setContactoEdit(null);
    setSelectedTipo(null);
    setSelectedOrigen(null);
    setSelectedPrioridad(null);
    setValorContacto("");
    setSujetoId(null);
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const payload = {
        idSujetoContacto: contactoEdit.idSujetoContacto,
        idSujetoContactoTipo: selectedTipo?.id || contactoEdit.idSujetoContactoTipo,
        idSujetoContactoOrigen: selectedOrigen?.id || contactoEdit.idSujetoContactoOrigen,
        idSujetoPrioridadContacto: selectedPrioridad?.id ?? null,
        idSujeto: sujetoId || contactoEdit.idSujeto,
        contacto: valorContacto,
      };

      console.log('Payload enviado para actualizar contacto:', payload);

      const res = await pathData('/SujetoContactoUpdate', payload);
      if (res?.isSuccess) {
        // Actualizar el estado directamente sin consultar la base de datos
        const updatedData = rawData.map((sujeto) => {
          if (sujeto.idSujeto === payload.idSujeto) {
            // Actualizar el contacto específico
            const updatedContactos = (sujeto.contactos || []).map((c) => {
              if (c.idSujetoContacto === payload.idSujetoContacto) {
                return {
                  ...c,
                  contacto: payload.contacto,
                  idSujetoContactoTipo: payload.idSujetoContactoTipo,
                  tipoContacto: selectedTipo?.label || c.tipoContacto,
                  idSujetoContactoOrigen: payload.idSujetoContactoOrigen,
                  origen: selectedOrigen?.label || c.origen,
                  idSujetoPrioridadContacto: payload.idSujetoPrioridadContacto,
                  prioridadNombre: selectedPrioridad?.label || null,
                };
              }
              return c;
            });
            return { ...sujeto, contactos: updatedContactos };
          }
          return sujeto;
        });

        // Actualizar rawData y procesar las filas
        setRawData(updatedData);
        processDataToRows(updatedData);
        
        setSnackbar({ open: true, msg: 'Contacto actualizado', severity: 'success' });
        handleCloseEdit();
      } else {
        setSnackbar({ open: true, msg: res?.error || 'Error actualizando contacto', severity: 'error' });
      }
    } catch (ex) {
      console.error('Error actualizando contacto:', ex);
      setSnackbar({ open: true, msg: 'Error actualizando contacto', severity: 'error' });
    }
    setSaving(false);
  };
  // Open add dialog for a given sujeto
  const handleOpenAdd = async (idSujeto) => {
    setAddSujetoId(idSujeto || null);
    setAddValorContacto("");
    setAddSelectedTipo(null);
    setAddSelectedOrigen(null);
    try {
      const tipos = await fetchSujetoContactoTipos();
      const origenes = await fetchSujetoContactoOrigenes();
      const prioridades = await fetchSujetoContactoPrioridades();
      const tiposList = (tipos || []).map(t => ({ id: t.idSujetoContactoTipo, label: t.descripcion, activo: t.activo }));
      const origenesList = (origenes || []).map(o => ({ id: o.idSujetoContactoOrigen, label: o.descripcion, activo: o.activo }));
      const prioridadesList = (prioridades || []).map(p => ({ id: p.idSujetoPrioridadContacto, label: p.descripcion, activo: p.activo }));
      setTiposContacto(tiposList);
      setOrigenesContacto(origenesList);
      setPrioridadesContacto(prioridadesList);
    } catch (ex) {
      console.error('Error cargando datos para agregar contacto:', ex);
    }
    setOpenAdd(true);
  };

  const handleCloseAdd = () => {
    setOpenAdd(false);
    setAddSujetoId(null);
    setAddSelectedTipo(null);
    setAddSelectedOrigen(null);
    setAddSelectedPrioridad(null);
    setAddValorContacto("");
  };

  const handleSaveAdd = async () => {
    // call backend to add a new contacto
    setSaving(true);
    try {
      const payload = {
        idSujetoContactoTipo: addSelectedTipo?.id || null,
        idSujetoContactoOrigen: addSelectedOrigen?.id || null,
        idSujetoPrioridadContacto: addSelectedPrioridad?.id || null,
        idSujeto: addSujetoId,
        contacto: addValorContacto,
      };
      const res = await postData('/SujetoContactoAlta', payload);
      if (res?.isSuccess) {
        // Actualizar el estado directamente sin consultar la base de datos
        const newContacto = {
          idSujetoContacto: res.value?.idSujetoContacto || Date.now(), // ID temporal si no viene del backend
          idSujetoContactoTipo: payload.idSujetoContactoTipo,
          tipoContacto: addSelectedTipo?.label || '',
          idSujetoContactoOrigen: payload.idSujetoContactoOrigen,
          origen: addSelectedOrigen?.label || '',
          idSujetoPrioridadContacto: payload.idSujetoPrioridadContacto,
          prioridadNombre: addSelectedPrioridad?.label || null,
          contacto: payload.contacto,
        };

        const updatedData = rawData.map((sujeto) => {
          if (sujeto.idSujeto === payload.idSujeto) {
            return {
              ...sujeto,
              contactos: [...(sujeto.contactos || []), newContacto],
            };
          }
          return sujeto;
        });

        // Actualizar rawData y procesar las filas
        setRawData(updatedData);
        processDataToRows(updatedData);
        
        setSnackbar({ open: true, msg: 'Contacto agregado', severity: 'success' });
        handleCloseAdd();
      } else {
        setSnackbar({ open: true, msg: res?.error || 'Error agregando contacto', severity: 'error' });
      }
    } catch (ex) {
      console.error('Error agregando contacto:', ex);
      setSnackbar({ open: true, msg: 'Error agregando contacto', severity: 'error' });
    }
    setSaving(false);
  };
  // Open delete confirmation dialog
  const handleDeleteContacto = (id) => {
    setDeleteId(id);
    setOpenDelete(true);
  };

  const handleCancelDelete = () => {
    setDeleteId(null);
    setOpenDelete(false);
  };

  const handleConfirmDelete = async () => {
    if (!deleteId) return;
    try {
      const res = await deleteData(`/SujetoContactoDelete/${deleteId}`);
      if (res?.isSuccess) {
        // Actualizar el estado directamente sin consultar la base de datos
        const updatedData = rawData.map((sujeto) => {
          return {
            ...sujeto,
            contactos: (sujeto.contactos || []).filter(c => c.idSujetoContacto !== deleteId),
          };
        });

        // Actualizar rawData y procesar las filas
        setRawData(updatedData);
        processDataToRows(updatedData);
        
        setSnackbar({ open: true, msg: 'Contacto eliminado', severity: 'success' });
      } else {
        setSnackbar({ open: true, msg: res?.error || 'Error eliminando contacto', severity: 'error' });
      }
    } catch (ex) {
      console.error('Error eliminando contacto:', ex);
      setSnackbar({ open: true, msg: 'Error eliminando contacto', severity: 'error' });
    }
    setDeleteId(null);
    setOpenDelete(false);
  };

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="secondary"
                borderRadius="lg"
                coloredShadow="light"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  Sujetos
                </MDTypography>
                <ManualAyuda seccion="Listado y ficha de Sujetos" />
              </MDBox>

              <MDBox pt={3} py={3} px={2}>
                <MDBox mb={2} display="flex" gap={2} flexWrap="wrap" alignItems="flex-start">
                  <MDBox>
                    <MDInput 
                      name="nombre" 
                      label="Apellido Nombre/Denominación" 
                      value={filters.nombre} 
                      onChange={handleFilterChange} 
                      onKeyDown={handleKeyDown} 
                    />
                  </MDBox>
                  
                  <MDBox>
                    <MDInput 
                      name="identificacion" 
                      label="CUIT (11 dígitos)" 
                      value={filters.identificacion} 
                      onChange={handleFilterChange} 
                      onKeyDown={handleKeyDown}
                      error={!!validationErrors.cuit}
                      inputProps={{ maxLength: 11 }}
                    />
                    {validationErrors.cuit && (
                      <MDTypography variant="caption" color="error" display="block" mt={0.5}>
                        {validationErrors.cuit}
                      </MDTypography>
                    )}
                  </MDBox>

                  <MDBox>
                    <MDInput 
                      name="dni" 
                      label="DNI (8 dígitos)" 
                      value={filters.dni} 
                      onChange={handleFilterChange} 
                      onKeyDown={handleKeyDown}
                      error={!!validationErrors.dni}
                      inputProps={{ maxLength: 8 }}
                    />
                    {validationErrors.dni && (
                      <MDTypography variant="caption" color="error" display="block" mt={0.5}>
                        {validationErrors.dni}
                      </MDTypography>
                    )}
                  </MDBox>

                  <MDBox>
                    <MDInput
                      name="email"
                      label="Email (exacto)"
                      value={filters.email}
                      onChange={handleFilterChange}
                      onKeyDown={handleKeyDown}
                    />
                  </MDBox>

                  <MDBox>
                    <MDInput
                      name="telefono"
                      label="Teléfono (10 dígitos, sin 0 ni 15)"
                      value={filters.telefono}
                      onChange={handleFilterChange}
                      onKeyDown={handleKeyDown}
                    />
                  </MDBox>

                  <MDBox>
                    <MDInput
                      name="domicilio"
                      label="Domicilio / Localidad (con otro filtro)"
                      value={filters.domicilio}
                      onChange={handleFilterChange}
                      onKeyDown={handleKeyDown}
                    />
                  </MDBox>

                  <MDButton variant="contained" color="info" onClick={handleSearch}>Buscar</MDButton>
                  <MDButton variant="contained" color="error" onClick={handleClear}>Limpiar</MDButton>
                </MDBox>

                {validationErrors.both && (
                  <MDBox mb={2}>
                    <MDTypography variant="caption" color="error" fontWeight="bold">
                      ⚠ {validationErrors.both}
                    </MDTypography>
                  </MDBox>
                )}
                
                <MDButton
                  onClick={handleAdd}
                  variant="gradient"
                  color="success"
                  endIcon={<BuildingFillAdd />}
                >
                  Agregar
                </MDButton>

                {loading ? (
                  <MDBox display="flex" justifyContent="center" alignItems="center" py={6}>
                    <CircularProgress />
                  </MDBox>
                ) : (
                  <DataTable
                    key={refreshKey}
                    table={{ columns, rows }}
                    isSorted={false}
                    showTotalEntries
                    canSearch={false}
                    noEndBorder
                    pagination={{ color: "info", variant: "gradient" }}
                    scrollHeight="60vh"
                  />
                )}
                <Dialog open={openEdit} onClose={handleCloseEdit} fullWidth maxWidth="sm">
                  <DialogTitle>Editar contacto</DialogTitle>
                  <DialogContent>
                    <MDBox display="flex" flexDirection="column" gap={2} mt={1}>
                      <Autocomplete
                        options={tiposContacto}
                        getOptionLabel={(option) => option.label || ""}
                        getOptionDisabled={(option) => !option.activo}
                        value={selectedTipo}
                        onChange={(e, newVal) => setSelectedTipo(newVal)}
                        renderInput={(params) => <TextField {...params} label="Tipo de contacto" />}
                      />

                      <Autocomplete
                        options={origenesContacto}
                        getOptionLabel={(option) => option.label || ""}
                        getOptionDisabled={(option) => !option.activo}
                        value={selectedOrigen}
                        onChange={(e, newVal) => setSelectedOrigen(newVal)}
                        renderInput={(params) => <TextField {...params} label="Origen" />}
                      />

                      <Autocomplete
                        options={prioridadesContacto}
                        getOptionLabel={(option) => option.label || ""}
                        getOptionDisabled={(option) => !option.activo}
                        value={selectedPrioridad}
                        onChange={(e, newVal) => setSelectedPrioridad(newVal)}
                        renderInput={(params) => <TextField {...params} label="Prioridad" />}
                      />

                      <TextField label="Contacto" value={valorContacto} onChange={(e) => setValorContacto(e.target.value)} fullWidth />
                    </MDBox>
                  </DialogContent>
                  <DialogActions>
                    <MDButton variant="contained" color="error" onClick={handleCloseEdit}>Cancelar</MDButton>
                    <MDButton variant="contained" color="success" onClick={handleSaveEdit} disabled={saving}>
                      {saving ? <CircularProgress size={18} color="inherit" /> : 'Guardar'}
                    </MDButton>
                  </DialogActions>
                </Dialog>
                {/* Add contacto dialog */}
                <Dialog open={openAdd} onClose={handleCloseAdd} fullWidth maxWidth="sm">
                  <DialogTitle>Agregar contacto</DialogTitle>
                  <DialogContent>
                    <MDBox display="flex" flexDirection="column" gap={2} mt={1}>
                      <Autocomplete
                        options={tiposContacto}
                        getOptionLabel={(option) => option.label || ""}
                        getOptionDisabled={(option) => !option.activo}
                        value={addSelectedTipo}
                        onChange={(e, newVal) => setAddSelectedTipo(newVal)}
                        renderInput={(params) => <TextField {...params} label="Tipo de contacto" />}
                      />

                      <Autocomplete
                        options={origenesContacto}
                        getOptionLabel={(option) => option.label || ""}
                        getOptionDisabled={(option) => !option.activo}
                        value={addSelectedOrigen}
                        onChange={(e, newVal) => setAddSelectedOrigen(newVal)}
                        renderInput={(params) => <TextField {...params} label="Origen" />}
                      />

                      <Autocomplete
                        options={prioridadesContacto}
                        getOptionLabel={(option) => option.label || ""}
                        getOptionDisabled={(option) => !option.activo}
                        value={addSelectedPrioridad}
                        onChange={(e, newVal) => setAddSelectedPrioridad(newVal)}
                        renderInput={(params) => <TextField {...params} label="Prioridad" />}
                      />

                      <TextField label="Contacto" value={addValorContacto} onChange={(e) => setAddValorContacto(e.target.value)} fullWidth />
                    </MDBox>
                  </DialogContent>
                  <DialogActions>
                    <MDButton variant="text" color="dark" onClick={handleCloseAdd}>Cancelar</MDButton>
                    <MDButton variant="gradient" color="info" onClick={handleSaveAdd} disabled={saving}>
                      {saving ? <CircularProgress size={18} color="inherit" /> : 'Guardar'}
                    </MDButton>
                  </DialogActions>
                </Dialog>
                {/* Delete confirmation dialog */}
                <Dialog open={openDelete} onClose={handleCancelDelete}>
                  <DialogTitle>Confirmar eliminación</DialogTitle>
                  <DialogContent>
                    ¿Está seguro que desea eliminar este contacto?
                  </DialogContent>
                  <DialogActions>
                    <MDButton variant="contained" color="warning" onClick={handleCancelDelete}>Cancelar</MDButton>
                    <MDButton variant="contained" color="error" onClick={handleConfirmDelete}>Eliminar</MDButton>
                  </DialogActions>
                </Dialog>

                <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleCloseSnackbar}>
                  <MuiAlert onClose={handleCloseSnackbar} severity={snackbar.severity} elevation={6} variant="filled">
                    {snackbar.msg}
                  </MuiAlert>
                </Snackbar>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default SujetoList;
